import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import {
  ACLOSET_CLAIM_RETURN_STATUS_SHORT_CODE_MAP,
  ACLOSET_ORDER_STATUS_SHORT_CODE_MAP,
  ACLOSET_SHOP_PLATFORM_TYPE_SHORT_CODE_MAP,
} from '@/MetadataType';
import type { ClaimCountsRequest } from '@/app/claim/return/hooks/useGetClaimsCounts';
import useGetClaimsCounts from '@/app/claim/return/hooks/useGetClaimsCounts';
import useGetOrderCounts from '@/app/orders/hooks/useGetOrderCounts';
import { queryKeys } from '@/app/queryKeys';
import { useShopAuth } from '@/state/userAuthContext';
import { addTimezoneToDate } from '@/utils/dateUtil';

import type { OrderCountsRequest } from './orders/common/type';

/**
 * 세컨솔드 주문 카운트 데이터 훅
 */
export default function useAclosetOrderCountData() {
  const queryClient = useQueryClient();

  const { shopID } = useShopAuth();

  const orderRequest: OrderCountsRequest = {
    from: addTimezoneToDate(dayjs().subtract(1, 'month').startOf('day')),
    to: addTimezoneToDate(dayjs().endOf('day')),
    searchPeriodType: 'orderDate',
    platformType: [ACLOSET_SHOP_PLATFORM_TYPE_SHORT_CODE_MAP.acloset],
    isDuplicated: false,
  };

  const claimRequest: ClaimCountsRequest = {
    from: addTimezoneToDate(dayjs().subtract(1, 'month').startOf('day')) ?? '',
    to: addTimezoneToDate(dayjs().endOf('day')) ?? '',
    searchPeriodType: 'returnRequestDate',
    platformType: [ACLOSET_SHOP_PLATFORM_TYPE_SHORT_CODE_MAP.acloset],
  };

  const 상품준비중_카운트_리퀘스트 = {
    ...orderRequest,
    orderStatus: [ACLOSET_ORDER_STATUS_SHORT_CODE_MAP['payment-done']],
    isOffer: false,
  };

  const 할인요청_카운트_리퀘스트 = {
    ...orderRequest,
    orderStatus: [ACLOSET_ORDER_STATUS_SHORT_CODE_MAP['payment-done']],
    isOffer: true,
  };

  const 배송준비중_카운트_리퀘스트 = {
    ...orderRequest,
    orderStatus: [ACLOSET_ORDER_STATUS_SHORT_CODE_MAP['shipping-pending']],
  };

  const 반품신청_카운트_리퀘스트 = {
    ...claimRequest,
    claimStatus: [
      ACLOSET_CLAIM_RETURN_STATUS_SHORT_CODE_MAP['request'],
      ACLOSET_CLAIM_RETURN_STATUS_SHORT_CODE_MAP['before-pickup'],
    ],
  };

  const refetchOption = {
    refetchInterval: 10 * 60 * 1000,
  };

  const refreshData = () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.orderCountsByShopID(shopID, 상품준비중_카운트_리퀘스트),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.orderCountsByShopID(shopID, 할인요청_카운트_리퀘스트),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.orderCountsByShopID(shopID, 배송준비중_카운트_리퀘스트),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.claimCountsByShopID(shopID, 반품신청_카운트_리퀘스트),
    });
  };

  const 상품준비중_카운트 =
    useGetOrderCounts(shopID, 상품준비중_카운트_리퀘스트, refetchOption).data?.orderCounts?.reduce(
      (total, curr) => total + (curr.orderCount ?? 0),
      0,
    ) ?? 0;

  const 할인요청_카운트 =
    useGetOrderCounts(shopID, 할인요청_카운트_리퀘스트, refetchOption).data?.orderCounts?.reduce(
      (total, curr) => total + (curr.orderCount ?? 0),
      0,
    ) ?? 0;

  const 배송준비중_카운트 =
    useGetOrderCounts(shopID, 배송준비중_카운트_리퀘스트, refetchOption).data?.orderCounts?.reduce(
      (total, curr) => total + (curr.orderCount ?? 0),
      0,
    ) ?? 0;

  const 반품신청_카운트 =
    useGetClaimsCounts(shopID, 반품신청_카운트_리퀘스트, refetchOption).data?.claimCounts?.reduce(
      (total, curr) => total + (curr.count ?? 0),
      0,
    ) ?? 0;

  const totalCount = 상품준비중_카운트 + 할인요청_카운트 + 배송준비중_카운트 + 반품신청_카운트;

  return {
    상품준비중_카운트,
    할인요청_카운트,
    배송준비중_카운트,
    반품신청_카운트,
    totalCount,
    refreshData,
  };
}
